import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router";
import clsx from "clsx";

import Header from "components/Header";
import Stepper from "components/Stepper";
import { JOIN_HAULPAY_STEP_LIST } from "constants/haulpay";
import { JOIN_HAULPAY_INVITED_STEP_LIST } from "constants/haulpay-invited-carrier";
import { routes } from "constants/routes";
import { useUserFeature } from "hooks/useUserFeature";
import MainLayout from "layouts/Main";
import { IPlaidAccount } from "models/HaulPay";
import { IUserInfoForm } from "models/User";
import { selectUserDetails } from "store/user/selector";
import JoinBankAccounts from "./modules/BankAccount";
import JoinCreateInfo from "./modules/CreateInfo";
import JoinDocs from "./modules/Docs";
import JoinSummary from "./modules/Summary";
import styles from "./style.module.scss";

interface ISectionProps {
  step: number;
  setStepCallback: (step: number) => void;
  createInfoFormData?: IUserInfoForm;
  permission?: boolean;
  submitData?: any;
  setSubmitData: (
    data: IUserInfoForm & {
      factoring_id?: string;
      bank_accounts?: IPlaidAccount[];
    }
  ) => void;
}

function SelectedSection({
  step,
  setStepCallback,
  submitData,
  setSubmitData
}: Readonly<ISectionProps>) {
  const { isInvitedCarrier } = useUserFeature();

  if (!isInvitedCarrier) {
    switch (step) {
      case 0:
        return (
          <JoinCreateInfo
            submitData={submitData}
            onNext={(data) => {
              setSubmitData(data);
              setStepCallback(1);
            }}
          />
        );

      case 1:
        return (
          <JoinBankAccounts
            onNext={(bank_accounts) => {
              setSubmitData({ ...submitData, bank_accounts: bank_accounts });
              setStepCallback(2);
            }}
          />
        );

      case 2:
        return (
          <JoinDocs
            submitData={submitData}
            onNext={(docs) => {
              setSubmitData({ ...submitData, docs: docs });
              setStepCallback(isInvitedCarrier ? 1 : 3);
            }}
          />
        );

      case 3:
        return <JoinSummary submitData={submitData} />;

      default:
        return null;
    }
  } else {
    switch (step) {
      case 0:
        return (
          <JoinDocs
            submitData={submitData}
            onNext={(docs) => {
              setSubmitData({ ...submitData, docs: docs });
              setStepCallback(1);
            }}
          />
        );

      case 1:
        return <JoinSummary submitData={submitData} />;

      default:
        return null;
    }
  }
}

export default function HaulPayJoin() {
  const navigate = useNavigate();
  const { isInvitedCarrier, isFactoringApproved } = useUserFeature();
  const [step, setStep] = useState(0);
  const [submitData, setSubmitData] = useState<
    IUserInfoForm & {
      factoring_id?: string;
    }
  >();
  const userDetails = useSelector(selectUserDetails);

  useEffect(() => {
    if (isFactoringApproved) {
      navigate(routes.HAULPAY_STATUS);
    }
  }, []);

  return (
    <MainLayout
      desktopTitle="Factoring"
      customHeader={<Header label="Factoring" isHideDesktop />}
      className="px-5 desktop:gap-10">
      <Stepper
        data={isInvitedCarrier ? JOIN_HAULPAY_INVITED_STEP_LIST : JOIN_HAULPAY_STEP_LIST}
        currentStep={step}
        setStep={setStep}
        canNavigate={false}
        className={clsx(styles.stepperWrapper, isInvitedCarrier && "mx-auto min-w-60")}
        containClass="desktop:!bg-background"
        isHideMobileTitle
      />

      {userDetails && (
        <SelectedSection
          step={step}
          setStepCallback={setStep}
          submitData={submitData}
          setSubmitData={setSubmitData}
        />
      )}
    </MainLayout>
  );
}
